<template>
  <div class="item-import-page">
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t('import_items') }}</h4>
        </div>
      </div>
      <div class="page-btn">
        <router-link to="/items" class="btn btn-added">
          <i class="ti ti-arrow-left me-1"></i>Back
        </router-link>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-lg-7">
            <h6 class="mb-2">{{ $t('import_notes_title') }}</h6>
            <ul class="import-notes mb-0">
              <li>Use one sheet named <strong>Items</strong>. Download the sample and keep the header row.</li>
              <li><strong>item_type*</strong> is a dropdown only: <code>NO_VARIANT</code>, <code>MULTI_VARIANT</code>, or <code>WARRANTY</code>.</li>
              <li><strong>NO_VARIANT</strong>: one row, leave all <code>attr_*</code> columns blank.</li>
              <li><strong>MULTI_VARIANT</strong>: same <code>item_code</code> on multiple rows; fill attribute columns per variant.</li>
              <li>
                <strong>WARRANTY</strong>: no-variant (one row, blank <code>attr_*</code>) or multi-variant (same
                <code>item_code</code>, fill <code>attr_*</code>). With stock: put serials in
                <code>serial_number</code> (one per line), set <code>stock</code> = serial count, and set
                <code>warranty_period_days</code>. Catalog-only: <code>with_stock=N</code>, blank serials.
              </li>
              <li>
                <strong>Category</strong> and <strong>sub-category</strong>: if the name already exists it is reused;
                otherwise a new one is created on <strong>{{ $t('confirm_import') }}</strong>.
              </li>
              <li>
                <strong>with_stock=Y</strong> uses <code>Main Warehouse</code> and <code>Default Supplier</code>
                automatically (created if missing). They are not shown in the grid or export file.
              </li>
              <li>
                <strong>Unit</strong> and <strong>vat_group</strong> must already exist (they are not auto-created).
                Attribute values are created if missing.
              </li>
              <li>
                <span class="legend-swatch duplicate"></span> Red = duplicate (file or database).
                <span class="legend-swatch disqualified ms-2"></span> Orange = disqualified (fix in the grid).
              </li>
              <li>Nothing is saved until you select rows and click <strong>{{ $t('confirm_import') }}</strong>.</li>
            </ul>
          </div>
          <div class="col-lg-5">
            <div class="d-flex flex-wrap gap-2 mb-3">
              <a
                href="/samples/item_import_sample.xlsx"
                class="btn btn-outline-primary btn-md"
                download
              >
                <i class="ti ti-file-spreadsheet me-1"></i>{{ $t('download_sample') }}
              </a>
            </div>
            <label class="form-label">{{ $t('browse_import_file') }}</label>
            <div class="input-group">
              <input
                ref="fileInput"
                type="file"
                class="form-control"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                @change="onFileChosen"
              />
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!file || loadingPreview"
                @click="loadFile"
              >
                <span v-if="loadingPreview" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('load_import_file') }}
              </button>
            </div>
            <small v-if="file" class="text-muted">{{ file.name }}</small>
            <div v-if="errorMessage" class="alert alert-danger mt-2 mb-0 py-2">{{ errorMessage }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div class="d-flex flex-wrap align-items-center gap-2">
            <span class="import-stat import-stat-ok">
              <i class="ti ti-circle-check"></i>
              {{ $t('import_legend_ok') }}: <strong>{{ summary.ok }}</strong>
            </span>
            <span class="import-stat import-stat-duplicate">
              <i class="ti ti-copy"></i>
              {{ $t('import_legend_duplicate') }}: <strong>{{ summary.duplicate }}</strong>
            </span>
            <span class="import-stat import-stat-disqualified">
              <i class="ti ti-alert-triangle"></i>
              {{ $t('import_legend_disqualified') }}: <strong>{{ summary.disqualified }}</strong>
            </span>
            <span v-if="revalidating" class="text-muted small">
              <span class="spinner-border spinner-border-sm me-1"></span>{{ $t('revalidating') }}
            </span>
          </div>
          <div class="d-flex flex-wrap gap-2 import-actions">
            <button
              type="button"
              class="btn btn-action-select"
              :disabled="!rows.length"
              @click="selectAllEligible"
            >
              <i class="ti ti-checkbox me-1"></i>{{ $t('select_all_eligible') }}
            </button>
            <button
              type="button"
              class="btn btn-action-clear"
              :disabled="!rows.length"
              @click="clearSelection"
            >
              <i class="ti ti-deselect me-1"></i>{{ $t('clear_selection') }}
            </button>
            <button
              type="button"
              class="btn btn-action-confirm"
              :disabled="!selectedCount || confirming"
              @click="confirmImport"
            >
              <span v-if="confirming" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="ti ti-database-import me-1"></i>
              {{ $t('confirm_import') }}
              <span class="confirm-count">{{ selectedCount }}</span>
            </button>
          </div>
        </div>

        <div v-if="!rows.length" class="text-center text-muted py-5">
          {{ $t('no_rows_loaded') }}
        </div>

        <div v-else class="import-grid-wrap">
          <table class="table table-bordered import-grid mb-0">
            <thead>
              <tr>
                <th class="col-check">
                  <input type="checkbox" :checked="allEligibleSelected" @change="toggleSelectAll($event)" />
                </th>
                <th>Status</th>
                <th>Issues</th>
                <th>Will create</th>
                <th>item_code*</th>
                <th>item_name*</th>
                <th>item_type*</th>
                <th>category*</th>
                <th>sub_category*</th>
                <th>unit*</th>
                <th>brand</th>
                <th>model_number</th>
                <th>vat_group</th>
                <th>discount</th>
                <th>with_stock</th>
                <th>sku*</th>
                <th>barcode*</th>
                <th>purchase_price*</th>
                <th>selling_price*</th>
                <th>stock</th>
                <th>serial_number</th>
                <th>warranty_period_days</th>
                <th
                  v-for="attr in attrNames"
                  :key="'h-' + attr"
                  class="attr-col"
                >
                  attr_{{ attr }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.row_id"
                :class="rowClass(row)"
              >
                <td class="col-check">
                  <input
                    type="checkbox"
                    :disabled="!row.eligible"
                    :checked="!!row.selected"
                    @change="toggleRow(row, $event)"
                  />
                </td>
                <td>
                  <span class="status-pill" :class="row.status">{{ row.status }}</span>
                </td>
                <td class="issues-cell" :title="(row.issues || []).join('; ')">
                  {{ (row.issues || []).join('; ') }}
                </td>
                <td class="will-create-cell" :title="(row.will_create || []).join('; ')">
                  {{ (row.will_create || []).join('; ') }}
                </td>
                <td><input v-model="row.item_code" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.item_name" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td>
                  <select v-model="row.item_type" class="form-select form-select-sm" @change="scheduleValidate">
                    <option value="NO_VARIANT">NO_VARIANT</option>
                    <option value="MULTI_VARIANT">MULTI_VARIANT</option>
                    <option value="WARRANTY">WARRANTY</option>
                  </select>
                </td>
                <td><input v-model="row.category" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.sub_category" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.unit" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.brand" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.model_number" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.vat_group" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.discount" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td>
                  <select v-model="row.with_stock" class="form-select form-select-sm" @change="scheduleValidate">
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </td>
                <td><input v-model="row.sku" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.barcode" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.purchase_price" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.selling_price" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.stock" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td>
                  <textarea
                    v-model="row.serial_number"
                    class="form-control form-control-sm serial-cell"
                    rows="2"
                    @change="scheduleValidate"
                  />
                </td>
                <td>
                  <input
                    v-model="row.warranty_period_days"
                    class="form-control form-control-sm"
                    @change="scheduleValidate"
                  />
                </td>
                <td v-for="attr in attrNames" :key="row.row_id + '-' + attr">
                  <input
                    class="form-control form-control-sm"
                    :value="(row.attributes && row.attributes[attr]) || ''"
                    @change="onAttrChange(row, attr, $event)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import apiUrls from '@/urls';

export default {
  name: 'ImportItem',
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      file: null,
      rows: [],
      attrNames: [],
      summary: { total: 0, ok: 0, duplicate: 0, disqualified: 0 },
      loadingPreview: false,
      revalidating: false,
      confirming: false,
      errorMessage: '',
      validateTimer: null,
    };
  },
  computed: {
    selectedCount() {
      return this.rows.filter((r) => r.selected && r.eligible).length;
    },
    allEligibleSelected() {
      const eligible = this.rows.filter((r) => r.eligible);
      return eligible.length > 0 && eligible.every((r) => r.selected);
    },
  },
  beforeUnmount() {
    if (this.validateTimer) clearTimeout(this.validateTimer);
  },
  methods: {
    rowClass(row) {
      return {
        'row-duplicate': row.status === 'duplicate',
        'row-disqualified': row.status === 'disqualified',
        'row-ok': row.status === 'ok',
      };
    },
    onFileChosen(event) {
      const chosen = event.target.files && event.target.files[0];
      this.file = chosen || null;
      this.errorMessage = '';
    },
    applyPreview(data) {
      this.attrNames = data.attr_names || [];
      this.rows = (data.rows || []).map((row) => ({
        ...row,
        serial_number: row.serial_number || '',
        warranty_period_days: row.warranty_period_days || '',
        attributes: { ...(row.attributes || {}) },
        issues: row.issues || [],
      }));
      this.summary = data.summary || { total: 0, ok: 0, duplicate: 0, disqualified: 0 };
    },
    async loadFile() {
      if (!this.file) return;
      this.loadingPreview = true;
      this.errorMessage = '';
      try {
        const form = new FormData();
        form.append('file', this.file);
        const { data } = await this.$axios.post(apiUrls.item.import_preview, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.applyPreview(data);
      } catch (err) {
        this.rows = [];
        this.attrNames = [];
        const detail = err?.response?.data;
        this.errorMessage =
          detail?.file?.[0] ||
          detail?.error ||
          detail?.detail ||
          'Failed to load file.';
      } finally {
        this.loadingPreview = false;
      }
    },
    onAttrChange(row, attr, event) {
      if (!row.attributes) row.attributes = {};
      const value = (event.target.value || '').trim();
      if (value) row.attributes[attr] = value;
      else delete row.attributes[attr];
      this.scheduleValidate();
    },
    scheduleValidate() {
      if (this.validateTimer) clearTimeout(this.validateTimer);
      this.validateTimer = setTimeout(() => this.revalidate(), 450);
    },
    payloadRows() {
      return this.rows.map((row) => ({
        row_id: row.row_id,
        excel_row: row.excel_row,
        item_code: row.item_code,
        item_name: row.item_name,
        item_type: row.item_type,
        category: row.category,
        sub_category: row.sub_category,
        unit: row.unit,
        brand: row.brand,
        model_number: row.model_number,
        vat_group: row.vat_group,
        discount: row.discount,
        with_stock: row.with_stock,
        sku: row.sku,
        barcode: row.barcode,
        purchase_price: row.purchase_price,
        selling_price: row.selling_price,
        stock: row.stock,
        serial_number: row.serial_number,
        warranty_period_days: row.warranty_period_days,
        attributes: row.attributes || {},
        selected: !!row.selected,
      }));
    },
    async revalidate() {
      if (!this.rows.length) return;
      this.revalidating = true;
      try {
        const { data } = await this.$axios.post(apiUrls.item.import_validate, {
          rows: this.payloadRows(),
          attr_names: this.attrNames,
        });
        this.applyPreview(data);
      } catch (err) {
        const detail = err?.response?.data;
        this.errorMessage = detail?.rows?.[0] || detail?.detail || 'Validation failed.';
      } finally {
        this.revalidating = false;
      }
    },
    toggleRow(row, event) {
      row.selected = !!event.target.checked && row.eligible;
    },
    toggleSelectAll(event) {
      const on = !!event.target.checked;
      this.rows.forEach((row) => {
        row.selected = on && row.eligible;
      });
    },
    selectAllEligible() {
      this.rows.forEach((row) => {
        row.selected = !!row.eligible;
      });
    },
    clearSelection() {
      this.rows.forEach((row) => {
        row.selected = false;
      });
    },
    async confirmImport() {
      this.confirming = true;
      this.errorMessage = '';
      try {
        const { data } = await this.$axios.post(apiUrls.item.import_confirm, {
          rows: this.payloadRows(),
        });
        if (data.preview) this.applyPreview(data.preview);
        const created = (data.created || []).length;
        const failed = (data.failed || []).length;
        if (created) {
          const done = new Set((data.created || []).map((c) => c.item_code));
          this.rows = this.rows.filter((r) => !done.has(r.item_code));
          this.summary = {
            total: this.rows.length,
            ok: this.rows.filter((r) => r.status === 'ok').length,
            duplicate: this.rows.filter((r) => r.status === 'duplicate').length,
            disqualified: this.rows.filter((r) => r.status === 'disqualified').length,
          };
        }
        if (created && failed === 0) {
          this.toast.success(`${this.$t('import_success')}: ${created} item(s)`);
        } else if (created && failed > 0) {
          this.toast.warning(
            `${this.$t('import_success')}: ${created} item(s), ${failed} failed`,
          );
        } else {
          const message =
            data.message ||
            (data.failed?.[0]?.error) ||
            'Nothing imported.';
          this.toast.error(typeof message === 'string' ? message : 'Nothing imported.');
        }
      } catch (err) {
        const detail = err?.response?.data;
        const message =
          detail?.error ||
          detail?.message ||
          detail?.rows?.[0] ||
          (detail?.failed?.[0]?.error) ||
          'Import failed.';
        this.toast.error(typeof message === 'string' ? message : 'Import failed.');
        if (detail?.preview) this.applyPreview(detail.preview);
      } finally {
        this.confirming = false;
      }
    },
  },
};
</script>

<style scoped>
.import-notes {
  padding-left: 1.1rem;
  font-size: 0.92rem;
  line-height: 1.55;
  color: #4b5563;
}
.legend-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  vertical-align: middle;
  margin-right: 4px;
}
.legend-swatch.duplicate {
  background: #fecaca;
  border: 1px solid #ef4444;
}
.legend-swatch.disqualified {
  background: #FE9F43;
  border: 1px solid #e8892a;
}
.import-grid-wrap {
  overflow: auto;
  max-height: min(70vh, 720px);
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.import-grid {
  font-size: 12px;
  margin: 0;
  min-width: 1400px;
}
.import-grid thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #1f4e79;
  color: #fff;
  white-space: nowrap;
  font-weight: 600;
  vertical-align: middle;
}
.import-grid thead th.attr-col {
  background: #833c0c;
}
.import-grid td,
.import-grid th {
  padding: 4px 6px;
  vertical-align: middle;
}
.import-grid .col-check {
  width: 36px;
  text-align: center;
}
.import-grid .form-control,
.import-grid .form-select {
  min-width: 88px;
  font-size: 12px;
  padding: 2px 6px;
  height: 28px;
}
.import-grid textarea.serial-cell {
  min-width: 220px;
  width: 220px;
  height: auto;
  min-height: 40px;
  line-height: 1.25;
  white-space: pre;
  overflow-x: auto;
}
.import-grid .issues-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #7c2d12;
}
.import-grid .will-create-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1d4ed8;
  font-size: 11px;
}
.row-duplicate {
  background: #fee2e2 !important;
}
.row-disqualified {
  background: #fff4e8 !important;
  box-shadow: inset 3px 0 0 #FE9F43;
}
.row-ok {
  background: #fff;
}
.status-pill {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}
.status-pill.ok {
  background: #dcfce7;
  color: #166534;
}
.status-pill.duplicate {
  background: #fecaca;
  color: #991b1b;
}
.status-pill.disqualified {
  background: #FE9F43;
  color: #fff;
}
.import-stat {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid transparent;
}
.import-stat i {
  font-size: 1rem;
}
.import-stat-ok {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}
.import-stat-duplicate {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fca5a5;
}
.import-stat-disqualified {
  background: #FE9F43;
  color: #fff;
  border-color: #e8892a;
  box-shadow: 0 1px 3px rgba(254, 159, 67, 0.45);
}
.import-stat-disqualified strong {
  color: #fff;
  font-size: 1rem;
}
.import-actions .btn {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.45rem 0.9rem;
  border-radius: 8px;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease, transform 0.1s ease;
}
.import-actions .btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
}
.import-actions .btn:not(:disabled):active {
  transform: translateY(0);
}
.btn-action-select {
  background: #eff6ff;
  border: 1px solid #3b82f6;
  color: #1d4ed8;
}
.btn-action-select:hover:not(:disabled) {
  background: #dbeafe;
  border-color: #2563eb;
  color: #1e40af;
}
.btn-action-clear {
  background: #f8fafc;
  border: 1px solid #94a3b8;
  color: #475569;
}
.btn-action-clear:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #64748b;
  color: #334155;
}
.btn-action-confirm {
  background: #212B36;
  border: 2px solid #212B36;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  padding: 0.65rem 1.35rem;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 14px rgba(33, 43, 54, 0.35);
}
.btn-action-confirm i {
  font-size: 1.15rem;
}
.btn-action-confirm:hover:not(:disabled) {
  background: #FE9F43;
  border-color: #FE9F43;
  color: #fff;
  box-shadow: 0 6px 18px rgba(254, 159, 67, 0.45);
}
.btn-action-confirm:disabled {
  background: #9ca3af;
  border-color: #9ca3af;
  color: #fff;
  opacity: 0.65;
  box-shadow: none;
}
.btn-action-confirm .confirm-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 999px;
  background: #FE9F43;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
}
.btn-action-confirm:hover:not(:disabled) .confirm-count {
  background: #fff;
  color: #FE9F43;
}
.btn-action-select:disabled,
.btn-action-clear:disabled {
  opacity: 0.55;
}
</style>
